/**
 * [T270-PY] DayOfWeekKpiCard — semantic `<button>` wrapper for a single
 * Day-of-Week KPI (weekday avg, weekend avg, top day, top category).
 *
 * Contract authority: `docs/superpowers/specs/2026-08-03-day-of-week-insights-design.md`
 *
 * Critical a11y rules:
 *   - Renders a real `<button type="button">` (NOT a styled `<div>`).
 *     WCAG 2.2 SC 4.1.2 + 2.5.3 require the platform role to match the visual
 *     affordance. The legacy `DayOfWeekBarSkin.tsx` KPI uses a plain `<div>`,
 *     which this component explicitly fixes.
 *   - `aria-label` MUST include the value so screen-reader users hear the
 *     number without entering the card.
 *   - `disabled` when loading OR when no `buildDrillHref` is wired.
 *   - `aria-live="polite"` on the inner value so updates are announced.
 *   - Touch target ≥ 44×44 px (Apple HIG + WCAG 2.5.5).
 *   - 4-channel a11y: color (`useDataToken`) + optional icon (`TrendingUp`/
 *     `TrendingDown`) + sign (handled by caller via `value` string) + text label.
 *   - Color is applied via inline `style={{ color }}` — NEVER a Tailwind
 *     `text-data-*` class because the `data` namespace is not registered
 *     in `frontend/tailwind.config.ts`.
 *   - Disabled state uses explicit muted text + border recolor and NEVER
 *     washes whole button opacity out of the panel.
 *
 * @module transactions/DayOfWeekKpiCard
 */
import type { ReactNode } from 'react';
import {
  TYPE_SCALE,
  useDataToken,
} from '@/lib/design/widgetTokens';

export type DayOfWeekKpiTone = 'neutral' | 'positive' | 'warning';

export interface DayOfWeekKpiCardProps {
  /** Visible label (e.g. "Weekday avg"). Required. */
  label: string;
  /** Visible value text (already formatted by caller — `fmt($1,820)` etc.). */
  value: string;
  /** Optional secondary text (e.g. "Mon–Fri mean"). Rendered as caption. */
  subValue?: string;
  /**
   * Semantic tone — drives the inline `color` style on the value.
   * Defaults to `'neutral'`. Never mix tones inside the same card.
   */
  tone?: DayOfWeekKpiTone;
  /** Optional icon (typically `<TrendingUp />` or `<TrendingDown />`). */
  icon?: ReactNode;
  /** Stable QA selector. Drives `data-testid`. */
  testId: string;
  /** Disabled when loading OR when `onActivate` is absent. */
  disabled?: boolean;
  /** Click activation. When omitted, the card renders `disabled=true`. */
  onActivate?: () => void;
}

/**
 * Single KPI cell — a true `<button>` with the value baked into the
 * accessible name and announced via `aria-live="polite"`.
 */
export function DayOfWeekKpiCard({
  label,
  value,
  subValue,
  tone = 'neutral',
  icon,
  testId,
  disabled = false,
  onActivate,
}: DayOfWeekKpiCardProps) {
  // Resolve the inline color string at render time. Tones map to
  // `useDataToken('positive' | 'warning' | 'neutral')`; `'muted'` is NOT a
  // registered data token, so we fall back to `neutral` for it.
  const valueColor = useDataToken(tone);
  const isDisabled = disabled || !onActivate;
  const ariaLabel = `${label}, ${value}${subValue ? `, ${subValue}` : ''}`;

  return (
    <button
      type="button"
      data-testid={testId}
      aria-label={ariaLabel}
      disabled={isDisabled}
      onClick={isDisabled ? undefined : onActivate}
      className={
        'group flex min-h-[44px] min-w-[44px] flex-col gap-1 rounded-md border bg-card px-3 py-2 text-left ' +
        'hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ' +
        'disabled:cursor-not-allowed disabled:hover:bg-card disabled:text-muted-foreground disabled:border-border ' +
        'transition-colors'
      }
    >
      <span
        className={`flex items-center gap-1 ${TYPE_SCALE.kpiLabel}`}
        aria-hidden="true"
      >
        {icon}
        <span className="break-words">{label}</span>
      </span>
      <span
        aria-live="polite"
        className={`${TYPE_SCALE.kpiHero} break-words`}
        style={{ color: isDisabled ? undefined : valueColor }}
      >
        {value}
      </span>
      {subValue && (
        <span className={`${TYPE_SCALE.caption} break-words`} aria-hidden="true">
          {subValue}
        </span>
      )}
    </button>
  );
}

export default DayOfWeekKpiCard;
